import { useAppDispatch } from "../store/hooks"
import { addToastNotification } from "../store/globalSlice"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import Navbar from "../Components/Navbar"
import { useState, useEffect, useRef } from "react"
import axios from "axios"
import { getUserCreds, host } from "@/utils/utils"

interface Friend {
  username: string
  email: string
  profileImage?: string
  gamesPlayed: number
  gamesWon: number
  rating: number
}

interface User {
  username: string
  email: string
  profileImage?: string
  gamesPlayed: number
  gamesWon: number
  rating: number
}

axios.defaults.baseURL = host

const Friends = () => {
  const dispatch = useAppDispatch()
  const [friends, setFriends] = useState<Friend[]>([])
  const [pendingInvites, setPendingInvites] = useState<Friend[]>([])
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [activeTab, setActiveTab] = useState<"friends" | "invites">("friends")
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Helper function to show toast notifications
  const showToast = (
    message: string,
    messageType: "success" | "error" | "info" = "info"
  ) => {
    dispatch(
      addToastNotification({
        message,
        messageType,
        type: "general",
      })
    )
  }

  // Get friends list
  const fetchFriends = async () => {
    try {
      setIsLoading(true)
      const token = getUserCreds().token

      const response = await axios.get("/api/friend/friends", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.friends && Array.isArray(response.data.friends)) {
        setFriends(response.data.friends)
      } else {
        console.error("Invalid friends response structure:", response.data)
        setFriends([])
      }
    } catch (error) {
      console.error("Error fetching friends:", error)
      setFriends([])
    } finally {
      setIsLoading(false)
    }
  }

  // Get pending invites
  const fetchPendingInvites = async () => {
    try {
      const token = getUserCreds().token

      const response = await axios.get("/api/friend/pending-invites", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (
        response.data.pendingInvites &&
        Array.isArray(response.data.pendingInvites)
      ) {
        setPendingInvites(response.data.pendingInvites)
      } else {
        console.error(
          "Invalid pending invites response structure:",
          response.data
        )
        setPendingInvites([])
      }
    } catch (error) {
      console.error("Error fetching pending invites:", error)
      setPendingInvites([])
    }
  }

  // Handle search input change with proper debouncing
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Clear results immediately if query is empty
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(query)
    }, 300)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  // Search users with better error handling
  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    try {
      setIsSearching(true)
      const token = getUserCreds().token
      const response = await axios.get(
        `/api/friend/search?q=${encodeURIComponent(query)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      // Set results directly without race condition check
      setSearchResults(response.data.users || [])
    } catch (error) {
      console.error("Error searching users:", error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // Send friend invite
  const sendInvite = async (friendEmail: string) => {
    try {
      const token = getUserCreds().token
      await axios.post(
        "/api/friend/send-invite",
        { friendEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      showToast("Friend invite sent successfully!")
      // Refresh friends list
      fetchFriends()
    } catch (error: any) {
      console.error("Error sending invite:", error)
      showToast(error.response?.data?.error || "Failed to send invite", "error")
    }
  }

  // Accept friend invite
  const acceptInvite = async (friendEmail: string) => {
    try {
      const token = getUserCreds().token
      await axios.post(
        "/api/friend/accept-invite",
        { friendEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      showToast("Friend invite accepted successfully!")
      // Refresh both lists
      fetchFriends()
      fetchPendingInvites()
    } catch (error: any) {
      console.error("Error accepting invite:", error)
      showToast(
        error.response?.data?.error || "Failed to accept invite",
        "error"
      )
    }
  }

  // Reject friend invite
  const rejectInvite = async (friendEmail: string) => {
    if (!confirm("Are you sure you want to reject this friend invite?")) {
      return
    }

    try {
      const token = getUserCreds().token
      await axios.post(
        "/api/friend/reject-invite",
        { friendEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      showToast("Friend invite rejected successfully!")
      // Refresh pending invites
      fetchPendingInvites()
    } catch (error: any) {
      console.error("Error rejecting invite:", error)
      showToast(
        error.response?.data?.error || "Failed to reject invite",
        "error"
      )
    }
  }

  // Remove friend
  const removeFriend = async (friendEmail: string) => {
    if (!confirm("Are you sure you want to remove this friend?")) {
      return
    }

    try {
      const token = getUserCreds().token
      await axios.delete("/api/friend/remove-friend", {
        data: { friendEmail },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      showToast("Friend removed successfully!")
      fetchFriends()
    } catch (error: any) {
      console.error("Error removing friend:", error)
      showToast(
        error.response?.data?.error || "Failed to remove friend",
        "error"
      )
    }
  }

  useEffect(() => {
    fetchFriends()
    fetchPendingInvites()
  }, [])

  return (
    <div
      className="w-screen h-screen bg-cover flex flex-col"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <Navbar />
      <div className="flex-1 pt-8 pl-[max(1rem,var(--safe-left))] pr-[max(1rem,var(--safe-right))] pb-[max(1rem,var(--safe-bottom))]">
        <div className="w-full max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-4xl font-bold text-white font-fourth">
              Friends
            </h2>
            <button
              onClick={() => setIsInviteDialogOpen(true)}
              className="flex items-center gap-2 bg-brown-700 hover:bg-brown-800 text-brown-200 font-bold py-2 px-6 rounded-lg shadow-lg transition-all duration-150"
            >
              Invite Friends
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveTab("friends")}
              className={`px-6 py-3 rounded-lg font-bold transition-all duration-150 relative ${
                activeTab === "friends"
                  ? "bg-brown-700 text-brown-200 shadow-lg"
                  : "bg-brown-700-light-1 text-brown-300 hover:bg-brown-700"
              }`}
            >
              Friends
              {friends.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-brown-200 bg-brown-600 rounded-full">
                  {friends.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("invites")}
              className={`px-6 py-3 rounded-lg font-bold transition-all duration-150 relative ${
                activeTab === "invites"
                  ? "bg-brown-700 text-brown-200 shadow-lg"
                  : "bg-brown-700-light-1 text-brown-300 hover:bg-brown-700"
              }`}
            >
              Pending Invites
              {pendingInvites.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full animate-pulse">
                  {pendingInvites.length}
                </span>
              )}
            </button>
          </div>

          {/* Content Section */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
            {/* Friends List */}
            {activeTab === "friends" && (
              <>
                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
                    <p className="mt-4 text-white font-fourth text-lg">
                      Loading friends...
                    </p>
                  </div>
                ) : friends.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-white font-fourth text-xl mb-2">
                      No friends yet
                    </p>
                    <p className="text-gray-300 font-fourth text-lg">
                      Invite some friends to get started!
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Debug: friends array length is {friends.length}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {friends.map((friend) => (
                      <div
                        key={friend.email}
                        className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg p-4 hover:bg-white/30 transition-all duration-150"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            {friend.profileImage ? (
                              <img
                                src={encodeURI(friend.profileImage)}
                                alt={friend.username}
                                className="w-12 h-12 rounded-full"
                              />
                            ) : (
                              <span className="text-xl font-bold text-white">
                                {friend.username[0].toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-lg font-fourth">
                              {friend.username}
                            </h3>
                            <p className="text-gray-300 text-sm font-fourth">
                              {friend.email}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm mb-4">
                          <div className="text-center">
                            <p className="text-gray-400 font-fourth">Games</p>
                            <p className="font-bold text-white">
                              {friend.gamesPlayed}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400 font-fourth">Wins</p>
                            <p className="font-bold text-white">
                              {friend.gamesWon}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400 font-fourth">Rating</p>
                            <p className="font-bold text-white">
                              {friend.rating}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFriend(friend.email)}
                          className="w-full bg-red-500 hover:bg-red-600 py-2 rounded-lg text-sm font-bold transition-all duration-150 text-white shadow-md"
                        >
                          Remove Friend
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Pending Invites */}
            {activeTab === "invites" && (
              <>
                {pendingInvites.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-white font-fourth text-xl">
                      No pending friend invites
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pendingInvites.map((invite) => (
                      <div
                        key={invite.email}
                        className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg p-4 hover:bg-white/30 transition-all duration-150"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                            {invite.profileImage ? (
                              <img
                                src={encodeURI(invite.profileImage)}
                                alt={invite.username}
                                className="w-12 h-12 rounded-full"
                              />
                            ) : (
                              <span className="text-xl font-bold text-white">
                                {invite.username[0].toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-lg font-fourth">
                              {invite.username}
                            </h3>
                            <p className="text-gray-300 text-sm font-fourth">
                              {invite.email}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm mb-4">
                          <div className="text-center">
                            <p className="text-gray-400 font-fourth">Games</p>
                            <p className="font-bold text-white">
                              {invite.gamesPlayed}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400 font-fourth">Wins</p>
                            <p className="font-bold text-white">
                              {invite.gamesWon}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400 font-fourth">Rating</p>
                            <p className="font-bold text-white">
                              {invite.rating}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => acceptInvite(invite.email)}
                            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 py-2 rounded-lg text-sm font-bold transition-all duration-150 text-white shadow-lg"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => rejectInvite(invite.email)}
                            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 py-2 rounded-lg text-sm font-bold transition-all duration-150 text-white shadow-lg"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Invite Dialog */}
      {isInviteDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-sm border border-white/30 rounded-lg w-full max-w-md h-[500px] flex flex-col shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 font-fourth">
                  Invite Friends
                </h3>
                <button
                  onClick={() => {
                    setIsInviteDialogOpen(false)
                    setSearchQuery("")
                    setSearchResults([])
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl transition-colors"
                >
                  ×
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search users by username or email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 font-fourth"
                />
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-y-auto p-6 pt-0">
                {isSearching ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-2 text-gray-600 text-sm font-fourth">
                      Searching...
                    </p>
                  </div>
                ) : searchQuery.trim() === "" ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm font-fourth">
                      Start typing to search for users
                    </p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm font-fourth">
                      No users found
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {searchResults.map((user) => (
                      <div
                        key={user.email}
                        className="flex items-center justify-between bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-all duration-150 border border-gray-200"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            {user.profileImage ? (
                              <img
                                src={encodeURI(user.profileImage)}
                                alt={user.username}
                                className="w-10 h-10 rounded-full"
                              />
                            ) : (
                              <span className="font-bold text-white">
                                {user.username[0].toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-gray-800 font-fourth">
                              {user.username}
                            </p>
                            <p className="text-gray-600 text-sm font-fourth">
                              {user.email}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => sendInvite(user.email)}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-4 py-1 rounded-lg text-sm font-bold transition-all duration-150 text-white shadow-md"
                        >
                          Invite
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Friends
